import * as React from "react";
import { useClinicState } from "../../../clinic/clinicStateContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { JsonObject } from "fe-common/graphql";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { featureFlagMap } from "fe-common/featureFlags/getFeatureFlags";
import { ConfigurationSettingListWithSource } from "@kahunmedical/common-configuration/dist/lib/settings";

/**
 * Use this hook to easily update the db with the clinic settings we use it in:
 * (old)V1 config, clinicTransitions, and clinicSettingsManagement
 * */
export const useUpdateClinicConfig = () => {
  const [
    {
      clinicData: { id },
    },
  ] = useClinicState();

  const [clinicConfigData, setClinicConfigData] = useState({});
  const [clinicMergedConfiguration, setClinicMergedConfiguration] = useState<
    ConfigurationSettingListWithSource
  >({});

  const [loading, setLoading] = useState(true);

  const dao = useClinicContext();

  const updateClinicData = useCallback(async () => {
    setLoading(true);
    const clinicInfo = await dao.clinicData(id);
    setClinicConfigData({ ...clinicInfo.config });
    setClinicMergedConfiguration(clinicInfo.mergedConfiguration);
    setLoading(false);
  }, [id, dao, setClinicConfigData, setClinicMergedConfiguration]);

  const setUpdatedClinicData = useCallback(
    async (clinicData) => {
      console.log("Calling set clinic", clinicData);
      return dao.setClinicConfig(id, clinicData).then((resultStatus) => {
        // Make sure to reload to get all latest updates to reduce the chance we hit a lock later
        updateClinicData();
        return resultStatus;
      });
    },
    [dao, id, updateClinicData],
  );

  // Make sure we update the state first with the last updated config (to avoid writing down an old setting
  useEffect(() => {
    // console.log("updateClinicConfig updating clinic on load!");
    updateClinicData();
  }, [updateClinicData]);

  const setSetting = useCallback(
    (setting: string, value: JsonObject) => {
      return new Promise((resolve, reject) =>
        setClinicConfigData((s) => {
          const updatedClinicData = {
            ...s,
            [setting]: value,
          };
          // console.log("updating new data", updatedClinicData);
          resolve(setUpdatedClinicData(updatedClinicData));
          return updatedClinicData;
        }),
      );
    },
    [setUpdatedClinicData],
  );

  const createStoreKeyHandler = useCallback(
    (key: ClinicConfigKeys) => (value: JsonObject) => {
      return setSetting(key, value);
    },
    [setSetting],
  );

  const getDefaultValueFor = useCallback((key: ClinicConfigKeys) => {
    let defaultSettings = {};
    try {
      // For feature flags read the current prod settings
      if (key === ClinicConfigKeys.FeatureFlags) {
        return featureFlagMap["production"];
      }
      // Read default values
      defaultSettings = require(`ask/src/components/clinic/clinicSettings/defaultSettings/${key}.json`);
    } catch (e) {
      console.log(e);
    }
    return defaultSettings;
  }, []);

  const createResetDefaultHandler = useCallback(
    (key: ClinicConfigKeys) => () => {
      setSetting(key, getDefaultValueFor(key));
    },
    [setSetting, getDefaultValueFor],
  );

  return useMemo(
    () => ({
      config: clinicConfigData,
      mergedConfiguration: clinicMergedConfiguration,
      getDefaultValueFor,
      createStoreKeyHandler,
      createResetDefaultHandler,
      isReady: !loading,
    }),
    [
      clinicConfigData,
      clinicMergedConfiguration,
      getDefaultValueFor,
      createStoreKeyHandler,
      createResetDefaultHandler,
      loading,
    ],
  );
};
